import React from "react";
import { Film } from "../../types";
import { Link } from "react-router";
import chatIcon from "../../assets/chat-bubble.svg";

type Props = {
  film: Film;
};

const FilmCard: React.FC<Props> = ({ film }) => {
  const desc = film.description ?? "";
  const shortDescription = desc.length > 100 ? desc.slice(0, 100) + "..." : desc;

  // Parse thumbnails if they're stored as string
  const thumbnails = React.useMemo(() => {
    if (!film.thumbnails) return null;
    if (typeof film.thumbnails === "string") {
      try {
        return JSON.parse(film.thumbnails);
      } catch {
        return null;
      }
    }
    return film.thumbnails;
  }, [film.thumbnails]);

  // Get the best thumbnail
  const thumbnail = React.useMemo(() => {
    if (thumbnails && Array.isArray(thumbnails)) {
      return (
        thumbnails[1]?.url ||
        thumbnails[0]?.url ||
        film.primary_image ||
        "https://placehold.co/400x600/1f2937/ffffff/png?text=Image+Not+Found"
      );
    }
    return film.primary_image || "https://placehold.co/400x600/1f2937/ffffff/png?text=Image+Not+Found";
  }, [thumbnails, film.primary_image]);

  return (
    <Link to={`/chat/${film.imdb_id}`} className="block group">
      <div className="bg-gray-900 border-2 border-red-600 rounded-xl overflow-hidden hover:shadow-[0_0_25px_rgba(255,0,0,0.6)] transition-all transform hover:scale-[1.02] duration-300 h-[450px] flex flex-col relative">
        {/* Image Container - Rectangle aspect ratio */}
        <div className="relative w-full h-[280px] bg-gray-800">
          <img
            src={thumbnail}
            alt={film.original_title || "Film poster"}
            className="object-cover w-full h-full"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = "https://placehold.co/400x600/1f2937/ffffff/png?text=Image+Not+Found";
            }}
          />

          {/* Type Badge */}
          <span className="absolute top-2 left-2 text-xs px-2 py-1 rounded bg-black/70 text-white font-medium backdrop-blur-sm">
            {film.type === "tvSeries" ? "TV Series" : "Movie"}
          </span>

          {/* Hover Chat Icon */}
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-red-600 rounded-full p-4 shadow-lg">
              <img src={chatIcon} alt="" className="w-8 h-8 invert brightness-200" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1">
          {/* Title and Description */}
          <div className="flex-1 min-h-0">
            <h3 className="text-white font-bold text-lg line-clamp-2 mb-2 leading-tight">{film.original_title}</h3>
            <p className="text-gray-400 text-sm line-clamp-2 leading-relaxed">{shortDescription}</p>
          </div>

          {/* Footer Info - Always visible at bottom */}
          <div className="mt-auto pt-3 border-t border-gray-700 flex items-center justify-between text-sm">
            <span className="text-gray-300 font-semibold">
              {film.start_year || (film.release_date ? new Date(film.release_date).getFullYear() : "N/A")}
            </span>
            <div className="flex items-center gap-3">
              {film.average_rating && (
                <span className="flex items-center gap-1 text-gray-300">
                  <span className="text-yellow-400">⭐</span>
                  <span className="font-semibold">{Number(film.average_rating).toFixed(1)}</span>
                </span>
              )}
              {film.runtime_minutes && <span className="text-gray-300 font-semibold">{film.runtime_minutes}min</span>}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default React.memo(FilmCard);
